import React, { useContext, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { LanguageContext } from '../hooks/useLanguage';
import { hero } from '../data/content';

gsap.registerPlugin(ScrollTrigger);

const Hero: React.FC = () => {
  const { lang } = useContext(LanguageContext);
  const bgRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const bg = bgRef.current;
    if (!bg) return;
    gsap.to(bg, {
      yPercent: 25,
      ease: 'none',
      scrollTrigger: {
        trigger: bg,
        start: 'top top',
        end: 'bottom top',
        scrub: true,
      },
    });
    return () => ScrollTrigger.getAll().forEach(st => st.kill());
  }, []);

  return (
    <section
      id="home"
      style={{
        minHeight: '100dvh',
        display: 'flex',
        alignItems: 'center',
        position: 'relative',
        overflow: 'hidden',
        background: '#0D0D0D',
        paddingTop: '72px',
      }}
    >
      {/* Background image — GSAP parallax */}
      <div
        ref={bgRef}
        style={{
          position: 'absolute',
          inset: '-25% 0',
          backgroundImage:
            'url(https://images.unsplash.com/photo-1621268277505-43ab0cf64ecd?auto=format&fit=crop&w=1800&q=80)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          zIndex: 0,
        }}
      />

      {/* Dark overlay */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background: 'linear-gradient(105deg, rgba(13,13,13,0.92) 50%, rgba(13,13,13,0.65) 100%)',
          zIndex: 1,
        }}
      />

      {/* KNX grid pattern */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          backgroundImage:
            'linear-gradient(rgba(200,169,110,0.06) 1px, transparent 1px), linear-gradient(90deg, rgba(200,169,110,0.06) 1px, transparent 1px)',
          backgroundSize: '60px 60px',
          zIndex: 2,
        }}
      />

      <div className="container" style={{ position: 'relative', zIndex: 3 }}>
        <motion.div
          initial={{ opacity: 0, y: 60 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, ease: [0.22, 1, 0.36, 1], delay: 0.2 }}
          style={{ maxWidth: '800px' }}
        >
          {/* Eyebrow label */}
          <motion.span
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.4 }}
            style={{
              fontFamily: 'var(--font-body)',
              fontSize: '11px',
              fontWeight: 600,
              letterSpacing: '5px',
              textTransform: 'uppercase',
              color: '#C8A96E',
              display: 'block',
              marginBottom: '24px',
            }}
          >
            KNX Certified Partner
          </motion.span>

          <h1
            style={{
              fontFamily: 'var(--font-display)',
              fontSize: 'clamp(36px, 5.5vw, 72px)',
              fontWeight: 700,
              lineHeight: 1.15,
              letterSpacing: '-1px',
              color: '#FFFFFF',
              marginBottom: '32px',
            }}
          >
            {hero.title[lang].split(' ').map((word, i) => (
              <span
                key={i}
                style={{ display: 'inline-block', overflow: 'hidden', verticalAlign: 'bottom', marginRight: '0.25em' }}
              >
                <motion.span
                  initial={{ y: '110%', opacity: 0 }}
                  animate={{ y: '0%', opacity: 1 }}
                  transition={{
                    delay: 0.5 + i * 0.07,
                    duration: 0.7,
                    ease: [0.22, 1, 0.36, 1],
                  }}
                  style={{ display: 'inline-block' }}
                >
                  {word}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.8 }}
            style={{
              fontFamily: 'var(--font-body)',
              fontSize: 'clamp(15px, 1.2vw, 18px)',
              color: '#AAAAAA',
              lineHeight: 1.8,
              maxWidth: '560px',
              marginBottom: '48px',
            }}
          >
            {hero.subtitle[lang]}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1 }}
            style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}
          >
            {/* Primary CTA */}
            <motion.a
              href="#contact"
              whileHover={{ background: '#C8A96E', color: '#0D0D0D' }}
              whileTap={{ scale: 0.98 }}
              style={{
                padding: '16px 40px',
                border: '1px solid #C8A96E',
                background: 'transparent',
                color: '#C8A96E',
                fontFamily: 'var(--font-body)',
                fontSize: '11px',
                fontWeight: 700,
                letterSpacing: '3px',
                textTransform: 'uppercase',
                transition: 'all 0.3s',
                cursor: 'pointer',
                display: 'inline-block',
              }}
            >
              {hero.cta[lang]}
            </motion.a>

            {/* Secondary CTA (ghost) */}
            <motion.a
              href="#services"
              whileHover={{ color: '#FFFFFF' }}
              whileTap={{ scale: 0.98 }}
              style={{
                padding: '16px 40px',
                color: '#AAAAAA',
                fontFamily: 'var(--font-body)',
                fontSize: '13px',
                fontWeight: 400,
                transition: 'color 0.3s',
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
              }}
            >
              {hero.cta2[lang]} →
            </motion.a>
          </motion.div>

          {/* KNX Partner badge */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.4 }}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '14px',
              marginTop: '40px',
              padding: '10px 16px 10px 10px',
              border: '1px solid rgba(200,169,110,0.2)',
              background: 'rgba(13,13,13,0.6)',
            }}
          >
            <div style={{ background: '#fff', padding: '6px 10px', display: 'flex', alignItems: 'center' }}>
              <img src="/Partner.jpg" alt="KNX Certified Partner" style={{ height: '28px', width: 'auto', display: 'block' }} />
            </div>
            <div>
              <div style={{
                fontFamily: 'var(--font-mono)',
                fontSize: '9px',
                fontWeight: 500,
                letterSpacing: '2px',
                color: '#C8A96E',
                textTransform: 'uppercase',
                lineHeight: 1,
              }}>
                {lang === 'es' ? 'Certificado Oficial' : lang === 'en' ? 'Officially Certified' : 'Certifié Officiel'}
              </div>
              <div style={{
                fontFamily: 'var(--font-mono)',
                fontSize: '9px',
                color: '#555',
                letterSpacing: '1.5px',
                textTransform: 'uppercase',
                marginTop: '3px',
                lineHeight: 1,
              }}>
                KNX Partner
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>

      {/* Gold accent bar at bottom */}
      <motion.div
        initial={{ scaleX: 0 }}
        animate={{ scaleX: 1 }}
        transition={{ delay: 1.2, duration: 1.5, ease: 'easeOut' }}
        style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          height: '3px',
          background: 'linear-gradient(90deg, #C8A96E, #E8C98A)',
          zIndex: 3,
          transformOrigin: 'left',
        }}
      />

      {/* Scroll indicator */}
      <motion.div
        animate={{ y: [0, 10, 0] }}
        transition={{ repeat: Infinity, duration: 2 }}
        style={{
          position: 'absolute',
          bottom: '30px',
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 3,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '4px',
        }}
      >
        <div style={{ width: '1px', height: '40px', background: 'rgba(200,169,110,0.4)' }} />
      </motion.div>
    </section>
  );
};

export default Hero;
